import * as Sentry from '@sentry/react';

/**
 * Interceptor for fetch API to automatically track API calls with Sentry
 * This wraps the native fetch function to add Sentry monitoring
 */
export const setupSentryFetchInterceptor = () => {
  const originalFetch = window.fetch;

  window.fetch = async (...args) => {
    const [resource, config] = args;
    const url = typeof resource === 'string' ? resource : (resource as Request).url;
    const method = config?.method || 'GET';
    const startTime = Date.now();

    // Start a span for this request
    return Sentry.startSpan(
      {
        name: `${method} ${url}`,
        op: 'http.client',
      },
      async () => {
        try {
          // Add breadcrumb for the request
          Sentry.addBreadcrumb({
            category: 'fetch',
            message: `${method} ${url}`,
            level: 'info',
            data: {
              method,
              url,
            },
          });

          // Make the actual request
          const response = await originalFetch(resource, config);
          const duration = Date.now() - startTime;

          // Track metrics
          Sentry.metrics.distribution('http.client.duration', duration, {
            unit: 'millisecond',
          });

          // Add breadcrumb for the response
          Sentry.addBreadcrumb({
            category: 'fetch',
            message: `${method} ${url} - ${response.status}`,
            level: response.ok ? 'info' : 'error',
            data: {
              method,
              url,
              status: response.status,
              duration,
            },
          });

          // If response is not ok, capture as warning
          if (!response.ok) {
            Sentry.captureMessage(
              `HTTP ${response.status}: ${method} ${url}`,
              'warning'
            );
          }

          return response;
        } catch (error) {
          const duration = Date.now() - startTime;

          // Add error breadcrumb
          Sentry.addBreadcrumb({
            category: 'fetch',
            message: `${method} ${url} - Failed`,
            level: 'error',
            data: {
              method,
              url,
              error: error instanceof Error ? error.message : 'Unknown error',
              duration,
            },
          });

          // Capture the exception
          Sentry.captureException(error, {
            contexts: {
              fetch: {
                url,
                method,
                duration,
              },
            },
          });

          throw error;
        }
      }
    );
  };
};

/**
 * Wrapper for axios/fetch clients to add Sentry monitoring
 */
export const createSentryApiClient = <T extends (...args: any[]) => Promise<any>>(
  apiFunction: T,
  options?: {
    name?: string;
    op?: string;
  }
): T => {
  return (async (...args: any[]) => {
    const operationName = options?.name || 'API Call';
    const operationType = options?.op || 'api.call';
    const startTime = Date.now();

    return Sentry.startSpan(
      {
        name: operationName,
        op: operationType,
      },
      async () => {
        try {
          const result = await apiFunction(...args);
          const duration = Date.now() - startTime;

          Sentry.metrics.distribution('api.call.duration', duration, {
            unit: 'millisecond',
          });

          return result;
        } catch (error) {
          const duration = Date.now() - startTime;

          Sentry.captureException(error, {
            contexts: {
              api: {
                operation: operationName,
                duration,
                args: JSON.stringify(args).substring(0, 1000), // Limit size
              },
            },
          });

          throw error;
        }
      }
    );
  }) as T;
};

/**
 * Higher-order function to wrap async functions with Sentry monitoring
 */
export const withSentryMonitoring = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  name: string
): T => {
  return (async (...args: any[]) => {
    return Sentry.startSpan(
      {
        name,
        op: 'function',
      },
      async () => {
        try {
          return await fn(...args);
        } catch (error) {
          Sentry.captureException(error, {
            contexts: {
              function: {
                name,
                args: JSON.stringify(args).substring(0, 500),
              },
            },
          });
          throw error;
        }
      }
    );
  }) as T;
};

